import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { Plus, FileText, ClipboardList } from "lucide-react";
import { convertProposalToWO } from "@/lib/api";
import { shortDate, money } from "@/lib/format";
import { btn } from "@/components/common/btn";
import SearchBar from "@/components/common/SearchBar";

// Proposals tab on the customer side-sheet. Lists every proposal
// scoped to the active site filter, lets you jump into the proposal
// editor, or one-click convert a pending proposal into a Work Order.
// Extracted from Clients.jsx (Feb 2026, iter180).
export function ClientProposalsTab({
  filteredProposals,
  selectedClient,
  selectedSiteId,
  clientSites,
  proposalStatusOptions,
  loadClientDetails,
  setActiveDetailTab,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const rows = q
    ? filteredProposals.filter((p) => [p.proposal_number, p.title, p.name, p.status, clientSites.find((s) => s.id === p.site_id)?.name]
        .some((v) => (v || "").toLowerCase().includes(q)))
    : filteredProposals;

  return (
    <TabsContent value="proposals" className="mt-4">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <SearchBar value={search} onChange={setSearch} placeholder="Search proposals..." testId="search-customer-proposals" />
        <button
          type="button"
          className={`${btn("write")} w-7 justify-center !px-0`} title="New proposal for this customer" aria-label="New proposal"
          onClick={() => {
            const siteId = selectedSiteId !== "all" ? selectedSiteId : "";
            navigate(`/proposals?client_id=${selectedClient.id}${siteId ? `&site_id=${siteId}` : ""}`);
          }}
          data-testid="btn-add-customer-proposal"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <span className="ml-auto text-xs text-slate-500 num">{rows.length} proposal{rows.length === 1 ? "" : "s"}</span>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p>No proposals {selectedSiteId !== "all" ? "for this site" : "yet"}</p>
        </div>
      ) : (
        <div className="data-table-container"><Table>
          <TableHeader>
            <TableRow>
              <TableHead className="th-std">Proposal</TableHead>
              <TableHead className="th-std">Customer</TableHead>
              <TableHead className="th-std">City / state</TableHead>
              <TableHead className="th-std">Date</TableHead>
              <TableHead className="th-std text-right">Value</TableHead>
              <TableHead className="th-std">Status</TableHead>
              <TableHead className="th-std w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((proposal) => {
              const site = clientSites.find((s) => s.id === proposal.site_id);
              const cityState = site
                ? [site.city, site.state].filter(Boolean).join(", ")
                : selectedClient
                  ? [selectedClient.city, selectedClient.state].filter(Boolean).join(", ")
                  : "";
              const bidDate = proposal.proposal_date || proposal.created_at?.split("T")[0] || "";
              return (
                <TableRow
                  key={proposal.id}
                  className="cursor-pointer table-row-hover"
                  data-testid={`customer-proposal-row-${proposal.id}`}
                >
                  <TableCell
                    className="font-medium text-xs text-brand hover:underline"
                    onClick={() => navigate(`/proposals?edit_id=${proposal.id}`)}
                  >
                    {proposal.name || "Untitled"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{selectedClient?.name || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{cityState || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500 tabular-nums">{shortDate(bidDate)}</TableCell>
                  <TableCell className="text-xs num">{money(proposal.value)}</TableCell>
                  <TableCell className="text-xs">
                    <span
                      className={`inline-flex w-[88px] items-center justify-center px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                        proposal.status === "won"
                          ? "bg-green-100 text-green-700"
                          : proposal.status === "lost"
                            ? "bg-red-100 text-red-700"
                            : proposal.status === "bid_submitted"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {proposalStatusOptions.find((s) => s.value === proposal.status)?.label || proposal.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {proposal.status !== "won" && proposal.status !== "lost" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Convert "${proposal.name}" to a Work Order? This will mark the proposal as Won.`)) return;
                          let converted = false;
                          try {
                            const res = await convertProposalToWO(proposal.id);
                            toast.success(`Work Order ${res.data.wo_number} created`);
                            converted = true;
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to convert");
                          }
                          // Refresh + tab switch happen outside the
                          // try so a refresh failure can't look like a
                          // conversion failure (iter168 fix #242).
                          if (converted) {
                            try {
                              await loadClientDetails(selectedClient, "workorders");
                            } catch {
                              setActiveDetailTab("workorders");
                            }
                          }
                        }}
                        data-testid={`btn-convert-wo-${proposal.id}`}
                        title="Convert to Work Order"
                      >
                        <ClipboardList className="w-3.5 h-3.5 text-brand" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table></div>
      )}
    </TabsContent>
  );
}
